import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useTranslation } from 'react-i18next'
import { useRouter } from '../../../hooks'
import { AUTOFIT_MAX, AUTOFIT_PADDING, COLUMN_MIN_WIDTHS, HEADER_H, INDENT_PX, OVERSCAN_ROWS, ROW_H, type ColumnId } from '../lib/constants'
import { autoFitWidth } from '../lib/column-autofit'
import { formatDays, formatIsoDate, todayIso } from '../lib/format'
import { centreDate, computeRange, pxPerDayFor, scrollLeftFor, timelineDays, timelineWidthFor, visibleDayWindow, xOf } from '../lib/geometry'
import { headerTiers } from '../lib/header-tiers'
import { formatPredecessors, type LocalLink } from '../lib/predecessors'
import { ancestorIds, visibleRowOf, visibleRows, type Tree, type TreeNode } from '../lib/tree'
import { useGanttStore, useGanttStoreApi } from '../store/ganttStore'
import { useBarDrag } from '../hooks/useBarDrag'
import { useCellEditing } from '../hooks/useCellEditing'
import { useGanttKeyboard, type GanttRowOp } from '../hooks/useGanttKeyboard'
import { useGanttScroll } from '../hooks/useGanttScroll'
import { useLinkDelete } from '../hooks/useLinkDelete'
import { useRowOps } from '../hooks/useRowOps'
import { useScheduleMutations } from '../hooks/useScheduleMutations'
import type { ScheduleActivity, ScheduleCalendar, ScheduleDetail, ScheduleExternalActivity } from '../types'
import ConfirmDialog from '../components/ConfirmDialog'
import GanttGrid from './GanttGrid'
import GanttGridHeader from './GanttGridHeader'
import GanttHoverProvider from './GanttHoverProvider'
import type { ForeignPredecessor } from './GanttRow'
import GanttTimeline from './GanttTimeline'
import GanttTimelineHeader from './GanttTimelineHeader'
import PrintDialog, { type PrintSource } from '../print/PrintDialog'
import RowContextMenu, { type RowContextMenuState } from './RowContextMenu'
import Splitter from './Splitter'
import { COLUMN_ORDER, gridWidthFor } from './gantt-columns'
import { GanttLayoutContext, useGanttLayout, type GanttLayout } from './gantt-layout'
import type { GanttOps } from './gantt-ops'
import type { RowOps } from './row-ops'

export { GanttLayoutContext, useGanttLayout }
export type { GanttLayout }
export type { RowOps } from './row-ops'

export interface GanttViewHandle {
  focusActivity(id: string, opts?: { openPanel?: boolean }): void
  scrollToToday(): void
  searchNext(): void
}

interface Props {
  detail: ScheduleDetail
  tree: Tree<ScheduleActivity>
  byId: Map<string, ScheduleActivity>
  calendarsById: Map<string, ScheduleCalendar>
  externalById: Map<string, ScheduleExternalActivity>
  canEdit: boolean
  /** Review fix (Important #4, spec §10): gates Add activity/milestone/sub-activity separately
   * from `canEdit` (a member can have EDIT without CREATE). */
  canCreate: boolean
  projectId: string
  refetchDetail: () => Promise<unknown>
  onFocusChange: (id: string | null) => void
  onOpenLog: (activityId?: string) => void
  /** Task 16/17: reports the current `GanttOps` bundle upwards (null on unmount) so `detail/index.tsx`
   * can hand it to consumers that render outside this component's tree — the toolbar's
   * `GanttEditingGroup` and the activity panel — through `GanttOpsContext`. */
  onOps?: (ops: GanttOps | null) => void
  /** Task 3 (keyboard row entry): `?` opens the shortcuts help dialog `detail/index.tsx` owns. */
  onShowShortcuts?: () => void
  /** Task 8 (print/PDF): the dialog is opened from the toolbar (a sibling of this component) but
   * rendered here, because only this component holds the laid-out rows, columns and range the
   * printable view snapshots. */
  printOpen?: boolean
  onPrintOpenChange?: (open: boolean) => void
}

const ZOOM_WHEEL_MS = 120

const GanttView = forwardRef<GanttViewHandle, Props>(({ detail, tree, byId, calendarsById, externalById, canEdit, canCreate, projectId, refetchDetail, onFocusChange, onOps, onShowShortcuts, printOpen, onPrintOpenChange }, ref) => {
  const { t, i18n } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const { scrollLeft, scrollTop, viewportWidth, viewportHeight } = useGanttScroll(scrollerRef)
  const { history } = useRouter()
  const api = useGanttStoreApi()
  const zoom = useGanttStore((s) => s.zoom)
  const columnWidths = useGanttStore((s) => s.columnWidths)
  const collapsedIds = useGanttStore((s) => s.collapsedIds)
  const criticalOnly = useGanttStore((s) => s.criticalOnly)
  const columnVisibility = useGanttStore((s) => s.columnVisibility)
  const selectedIds = useGanttStore((s) => s.selectedIds)
  const focusedId = useGanttStore((s) => s.focusedId)
  const selectedLinkId = useGanttStore((s) => s.selectedLinkId)
  const storeScheduleId = useGanttStore((s) => s.scheduleId)
  // Read for the print snapshot only: the print dialog opens mirroring the toolbar's own toggles.
  const showBaseline = useGanttStore((s) => s.showBaseline)
  const showCriticalPath = useGanttStore((s) => s.showCriticalPath)
  const scheduleId = detail.schedule.id
  const today = useMemo(() => todayIso(), [])

  const rows = useMemo(
    () => visibleRows(tree, collapsedIds, criticalOnly ? (n: TreeNode<ScheduleActivity>) => !n.activity.isSummary && n.activity.isCritical : undefined),
    [tree, collapsedIds, criticalOnly],
  )
  const rowIndexById = useMemo(() => {
    const map = new Map<string, number>()
    rows.forEach((r) => map.set(r.node.activity.id, r.index))
    return map
  }, [rows])
  const visibleOrder = useMemo(() => rows.map((r) => r.node.activity.id), [rows])

  // Task 10: local + foreign predecessor tokens for the grid's Predecessors column (§9.1),
  // keyed by the successor (i.e. the row that shows them).
  const predecessorsByActivity = useMemo(() => {
    const map = new Map<string, { local: LocalLink[]; foreign: ForeignPredecessor[] }>()
    detail.dependencies.forEach((d) => {
      if (!byId.has(d.successorId)) return
      let entry = map.get(d.successorId)
      if (!entry) { entry = { local: [], foreign: [] }; map.set(d.successorId, entry) }
      if (byId.has(d.predecessorId)) {
        entry.local.push({ id: d.id, predecessorId: d.predecessorId, type: d.type, lagDays: d.lagDays })
      } else {
        const ext = externalById.get(d.predecessorId)
        if (ext) entry.foreign.push({ scheduleName: ext.scheduleName, wbsCode: ext.wbsCode, name: ext.name, id: ext.id, scheduleId: ext.scheduleId })
      }
    })
    return map
  }, [detail.dependencies, byId, externalById])

  // Task 16: the mirror of `predecessorsByActivity` — keyed by the *predecessor*, listing what it
  // is a predecessor OF — for the row context menu's "Go to successor" submenu. Local successors
  // only need an id (looked up in `byId` for the label); foreign ones reuse `ForeignPredecessor`'s
  // shape (it already carries `id`/`scheduleId` for navigation) even though they're successors here.
  const successorsByActivity = useMemo(() => {
    const map = new Map<string, { local: string[]; foreign: ForeignPredecessor[] }>()
    detail.dependencies.forEach((d) => {
      if (!byId.has(d.predecessorId)) return
      let entry = map.get(d.predecessorId)
      if (!entry) { entry = { local: [], foreign: [] }; map.set(d.predecessorId, entry) }
      if (byId.has(d.successorId)) {
        entry.local.push(d.successorId)
      } else {
        const ext = externalById.get(d.successorId)
        if (ext) entry.foreign.push({ scheduleName: ext.scheduleName, wbsCode: ext.wbsCode, name: ext.name, id: ext.id, scheduleId: ext.scheduleId })
      }
    })
    return map
  }, [detail.dependencies, byId, externalById])

  // Cross-schedule ghosts (§8.6), keyed by the row that actually *displays* the local endpoint —
  // itself, or the outermost collapsed ancestor standing in for it (`visibleRowOf`), so a ghost
  // whose local endpoint is hidden inside a collapsed summary relocates to the summary's row
  // instead of disappearing while `DependencyLayer`'s line still routes there. Several hidden
  // children can map to the same summary row, hence the array value.
  const ghostsByRow = useMemo(() => {
    const map = new Map<string, ScheduleExternalActivity[]>()
    detail.dependencies.forEach((d) => {
      const predLocal = byId.has(d.predecessorId)
      const succLocal = byId.has(d.successorId)
      if (predLocal === succLocal) return // both local (ordinary link) or both foreign (not ours to draw)
      const localId = predLocal ? d.predecessorId : d.successorId
      const ext = externalById.get(predLocal ? d.successorId : d.predecessorId)
      if (!ext) return
      const rowId = visibleRowOf(tree, collapsedIds, localId)
      const list = map.get(rowId)
      if (list) { if (list.every((e) => e.id !== ext.id)) list.push(ext) } else map.set(rowId, [ext])
    })
    return map
  }, [detail.dependencies, byId, externalById, tree, collapsedIds])

  const onGhostClick = useCallback((ext: ScheduleExternalActivity) => {
    history.push(`/project/${projectId}/schedule/${ext.scheduleId}?activity=${ext.id}`)
  }, [history, projectId])

  // Task 16: same cross-schedule navigation as `onGhostClick`, for the context menu's foreign
  // "Go to predecessor/successor" entries (a `ForeignPredecessor`, not a full `ScheduleExternalActivity`).
  const goToExternal = useCallback((ref: { id: string; scheduleId: string }) => {
    history.push(`/project/${projectId}/schedule/${ref.scheduleId}?activity=${ref.id}`)
  }, [history, projectId])

  // BUG-SCH-UI2-02 fix: this used to build `dates` via `.reduce((acc, a) => acc.concat([...]), [])` —
  // `Array.prototype.concat` copies the whole accumulator on every iteration, making that an O(n²)
  // pass over the activity list (measured ~9-10ms of the ~15ms this component's own memos took on a
  // 5000-activity/4910-link schedule, PERF-01's fixture). A flat `forEach`+`push` is O(n) and behaves
  // identically — `computeRange` only reads min/max over the (filtered, order-independent) result.
  const range = useMemo(() => {
    const dates: (string | null)[] = []
    detail.activities.forEach((a) => { dates.push(a.start, a.finish, a.baselineStart, a.baselineFinish) })
    detail.externalActivities.forEach((e) => { dates.push(e.start, e.finish) })
    return computeRange({ scheduleStart: detail.schedule.startDate, today, dates })
  }, [detail, today])

  const columns = useMemo(() => COLUMN_ORDER.filter((c) => columnVisibility[c]), [columnVisibility])
  // Task 2 (Freelo #32253593): the grid pane width is derived from the per-column widths, never
  // stored on its own — the splitter and the header's per-column resize handles both write into
  // `columnWidths` (see `onSetGridWidth`/`onResizeColumn` below), and this just re-sums them.
  const gridWidth = useMemo(() => gridWidthFor(columns, columnWidths), [columns, columnWidths])
  // Task 8 (print/PDF): the snapshot the printable view works from. Built only while the print
  // dialog is open — every input below is already memoised for the Gantt itself, so this costs
  // nothing on the normal render path.
  const printSource = useMemo<PrintSource | null>(
    () => (printOpen
      ? {
        schedule: detail.schedule, rows, tree, byId, dependencies: detail.dependencies, collapsedIds,
        predecessorsByActivity, columns, columnWidths, range, today, rowIndexById,
        hasBaseline: !!detail.schedule.activeBaselineId, showBaseline, showCriticalPath,
      }
      : null),
    [printOpen, detail.schedule, detail.dependencies, rows, tree, byId, collapsedIds, predecessorsByActivity, columns, columnWidths, range, today, rowIndexById, showBaseline, showCriticalPath],
  )
  const timelineViewport = Math.max(0, viewportWidth - gridWidth)
  const pxPerDay = useMemo(() => pxPerDayFor(zoom, range.totalDays, timelineViewport), [zoom, range.totalDays, timelineViewport])
  // Freelo #32277855: range + trailing-label gutter, so the last label is reachable at every zoom.
  const timelineWidth = timelineWidthFor(range.totalDays, pxPerDay, timelineViewport)
  const contentHeight = HEADER_H + rows.length * ROW_H

  // Review fix (Minor #11): computed once here (both tiers, locale-aware) and shared via
  // `GanttLayout` — `GanttTimeline` (gridlines) and `GanttTimelineHeader` (labels) each used to
  // call `headerTiers` themselves on every horizontal window change, building four
  // `Intl.DateTimeFormat`s twice for the same result.
  const { fromDay, toDay } = useMemo(
    () => visibleDayWindow(scrollLeft, timelineViewport, pxPerDay, timelineDays(range.totalDays, pxPerDay)),
    [scrollLeft, timelineViewport, pxPerDay, range.totalDays],
  )
  const tiers = useMemo(
    () => headerTiers(zoom, range.start, fromDay, toDay, pxPerDay, t('date_locale') || i18n.language),
    [zoom, range.start, fromDay, toDay, pxPerDay, t, i18n.language],
  )

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => ROW_H,
    overscan: OVERSCAN_ROWS,
    scrollMargin: HEADER_H,
    scrollPaddingStart: HEADER_H,
  })

  // keep the centre date across zoom / range changes
  const previous = useRef({ pxPerDay, rangeStart: range.start })
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    if (previous.current.pxPerDay !== pxPerDay || previous.current.rangeStart !== range.start) {
      const centre = centreDate(el.scrollLeft, timelineViewport, previous.current.pxPerDay, previous.current.rangeStart)
      previous.current = { pxPerDay, rangeStart: range.start }
      el.scrollLeft = scrollLeftFor(centre, timelineViewport, pxPerDay, range.start)
    }
  }, [pxPerDay, range.start, timelineViewport])

  // initial horizontal position: today (if inside the range) at 20 % of the viewport, else the schedule start
  const initialised = useRef(false)
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el || initialised.current || timelineViewport === 0) return
    initialised.current = true
    const anchor = today >= range.start && today <= range.end ? today : detail.schedule.startDate
    el.scrollLeft = scrollLeftFor(anchor, timelineViewport, pxPerDay, range.start, 0.2)
  }, [timelineViewport, pxPerDay, range, today, detail.schedule.startDate])

  // ctrl/cmd + wheel zooms; React's onWheel is passive, so preventDefault needs a native listener
  const lastZoom = useRef(0)
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const now = Date.now()
      if (now - lastZoom.current < ZOOM_WHEEL_MS) return
      lastZoom.current = now
      api.getState().zoomStep(e.deltaY < 0 ? 1 : -1)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [api])

  const focusRow = useCallback((index: number, extend: boolean) => {
    const row = rows[Math.min(rows.length - 1, Math.max(0, index))]
    if (!row) return
    api.getState().select(row.node.activity.id, extend ? 'range' : 'replace', visibleOrder)
    virtualizer.scrollToIndex(row.index, { align: 'auto' })
  }, [rows, visibleOrder, api, virtualizer])

  // Scrolling to a row has to wait for the render that expanded its ancestors, so `focusActivity`
  // only files a request and this effect performs it. The request also remembers the `tree` it was
  // filed against: Task 16's create/delete continuations call `focusActivity` synchronously right
  // after their mutation's promise resolves, but the Apollo cache write that mutation just made
  // reaches `props.tree`/`rowIndexById` one *extra* microtask later (confirmed against
  // `@apollo/react-hooks`'s `Promise.resolve().then(forceUpdate)` deferral) — so the very first run
  // of this effect for such a request sees the row missing from the still-stale `rowIndexById`. If
  // the id is at least known to `byId` (the full, unfiltered activity map) and `tree` hasn't
  // changed since the request was filed, the request stays pending for the next render instead of
  // being dropped. Once `tree` *has* changed and the row is still not in `rowIndexById` (deleted,
  // or filtered out by `criticalOnly`), it gives up rather than retrying forever.
  const [scrollRequest, setScrollRequest] = useState<{ id: string; seq: number; tree: Tree<ScheduleActivity> } | null>(null)
  const requestSeq = useRef(0)
  useEffect(() => {
    if (!scrollRequest) return
    const index = rowIndexById.get(scrollRequest.id)
    if (index == null) {
      if (byId.has(scrollRequest.id) && tree === scrollRequest.tree) return // same stale tree the request was filed against — wait for the fresh one
      setScrollRequest(null) // a fresh tree already arrived (or the id is gone) and it's still not visible — give up
      return
    }
    const el = scrollerRef.current
    virtualizer.scrollToIndex(index, { align: 'center' })
    const activity = byId.get(scrollRequest.id)
    if (el && activity && timelineViewport > 0) {
      const x = xOf(activity.start, range.start, pxPerDay)
      if (x < el.scrollLeft + 40 || x > el.scrollLeft + timelineViewport - 40) {
        el.scrollLeft = scrollLeftFor(activity.start, timelineViewport, pxPerDay, range.start, 0.2)
      }
    }
    setScrollRequest(null)
  }, [scrollRequest, rowIndexById, byId, tree, range.start, pxPerDay, timelineViewport, virtualizer])

  // The page mirrors `focusedId` into ?activity=, which comes straight back as a
  // deep-link focus request; that echo must not collapse a range selection.
  const reportedFocus = useRef<string | null>(null)

  const focusActivity = useCallback((id: string, opts?: { openPanel?: boolean }) => {
    const st = api.getState()
    // Review fix (parked minor): the early return used to fire whenever the row was already
    // focused, even when this call also asked to open the panel — so a `?activity=<focused id>`
    // deep link (or the activity log's "focus" action) landing on the already-focused row with the
    // panel closed did nothing. Only skip the rest when there is truly nothing left to do.
    const alreadyFocused = reportedFocus.current === id && st.focusedId === id
    const needsPanelOpen = !!(opts && opts.openPanel && !st.panelOpen)
    if (alreadyFocused && !needsPanelOpen) return
    const hidden = ancestorIds(tree, id).filter((a) => st.collapsedIds.has(a))
    if (hidden.length) st.setCollapsed(hidden, false)
    st.select(id, 'replace', visibleOrder)
    if (opts && opts.openPanel) st.setPanelOpen(true)
    requestSeq.current += 1
    setScrollRequest({ id, seq: requestSeq.current, tree })
  }, [api, tree, visibleOrder])

  // `detail/index.tsx` creates a fresh gantt store per schedule id (`createGanttStore(scheduleId)`),
  // which starts with `focusedId: null`. Navigating client-side to a different schedule (e.g. a
  // cross-schedule ghost click, Task 10) swaps the store — and therefore resets `focusedId` to null —
  // in the same render where `detail`/`tree` still describe the *previous* schedule (Apollo hasn't
  // delivered the new query yet). Without this, the echo effect below sees that reset, believes the
  // user cleared focus, and wipes the `?activity=` query the navigation just set before the deep-link
  // effect in `detail/index.tsx` gets a chance to read it once the new schedule's data arrives. Reading
  // `scheduleId` off the store itself (not the `detail` prop) keys this to the exact render the store
  // swaps in, and running before the echo effect (declared first) re-arms its guard in time.
  useEffect(() => {
    reportedFocus.current = focusedId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeScheduleId])

  // report the focused row upwards (the page mirrors it into ?activity=), never on mount
  useEffect(() => {
    if (reportedFocus.current === focusedId) return
    reportedFocus.current = focusedId
    onFocusChange(focusedId)
  }, [focusedId, onFocusChange])

  const searchNext = useCallback(() => {
    const st = api.getState()
    const query = st.search.trim().toLowerCase()
    if (!query) return
    const from = st.focusedId ? tree.order.findIndex((n) => n.activity.id === st.focusedId) + 1 : 0
    const ordered = tree.order.slice(from).concat(tree.order.slice(0, from))
    const hit = ordered.filter((n) => n.activity.name.toLowerCase().indexOf(query) >= 0 || (n.activity.owner ?? '').toLowerCase().indexOf(query) >= 0)[0]
    if (hit) focusActivity(hit.activity.id)
  }, [api, tree, focusActivity])

  const scrollToToday = useCallback(() => {
    const el = scrollerRef.current
    if (el) el.scrollLeft = scrollLeftFor(today, timelineViewport, pxPerDay, range.start, 0.3)
  }, [today, timelineViewport, pxPerDay, range.start])

  useImperativeHandle(ref, () => ({ focusActivity, scrollToToday, searchNext }), [focusActivity, scrollToToday, searchNext])

  // Task 16: the single mutation queue for the whole detail page, hoisted here (out of
  // `useCellEditing`, which used to instantiate its own) so cell edits and row operations
  // serialise behind the same promise chain and patch the same cache.
  const { run } = useScheduleMutations(scheduleId, refetchDetail)

  // Task 3 (keyboard row entry): `useCellEditing` needs an `insertAfter` callback for Shift+Enter/
  // Insert commits, but it's called (below) *before* `useRowOps` computes `rowOps.addActivity` —
  // reordering the hooks would change the existing `useCellEditing`/`useRowOps` dependency shape,
  // so this stable wrapper reads from a ref that gets pointed at the real thing once `rowOps`
  // exists further down (still within the same render, so there's no one-render lag).
  const insertAfterRef = useRef<((id: string) => void) | undefined>(undefined)
  const insertAfter = useCallback((id: string) => { insertAfterRef.current?.(id) }, [])

  // Task 14 — inline cell editing (spec B §9.3): pending map, mutations and focus movement all
  // live in this hook (it grew GanttView past the file's ~400-line guideline).
  const { editingCell, pending, startEdit, startEditEdge, onCommitCell, onCancelCell } = useCellEditing({
    scheduleId, byId, canEdit, columns, rows, rowIndexById, visibleOrder, run, tree, predecessorsByActivity,
    insertAfter: canCreate ? insertAfter : undefined,
  })

  // Focus fix (found during Task 3's browser check, pre-existing): closing a cell editor by Escape or
  // a commit unmounts the focused <input>, so keyboard focus falls back to <body> and every grid
  // shortcut is dead until the user clicks a row again — fatal for keyboard-only entry. When the
  // editor closes and nothing else took focus, hand it back to the scroller. `document.activeElement
  // === document.body` is the guard: a blur-commit caused by clicking a panel field leaves focus in
  // that field and must not be stolen.
  const wasEditingRef = useRef(false)
  useEffect(() => {
    const isEditing = !!editingCell
    if (wasEditingRef.current && !isEditing && document.activeElement === document.body) {
      scrollerRef.current?.focus({ preventScroll: true })
    }
    wasEditingRef.current = isEditing
  }, [editingCell])

  // Task 16 — row operations (spec B §9.5): add/indent/outdent/move/delete, computed from
  // `focusedId` / `selectedIds` and the tree, shared with the toolbar's `GanttEditingGroup`
  // (through the `onOps` callback below) and this component's own `RowContextMenu`. Extracted into
  // its own hook (review fix) once this component crossed the ~400-line guideline.
  const { rowOps, deleteTargets, deleteCount, deleting, confirmDelete, cancelDelete } = useRowOps({
    scheduleId, tree, byId, rows, rowIndexById, selectedIds, focusedId, run, focusActivity, canCreate,
  })
  insertAfterRef.current = canCreate ? (id: string) => rowOps.addActivity('task', id) : undefined

  // Task 18 — bar resize + link drag (spec B §9.6): the whole gesture lives in this hook (reads/
  // writes the store's `drag`), `GanttTimeline` only routes pointer events into it and renders its
  // live preview.
  const barDrag = useBarDrag({
    scheduleId, canEdit, byId, rows, rowIndexById, ghostsByRow, range, pxPerDay,
    calendarsById, defaultCalendarId: detail.schedule.defaultCalendarId, scrollLeft, scrollTop, run,
  })

  // Task 18 — link selection + delete (spec B §8.5): confirm dialog for the link `Delete` selected
  // via `useGanttKeyboard`'s `onRowOp` below. Extracted into its own hook in fix round 1 (this file
  // had crossed the ~500-line guideline) alongside the pre-existing `useRowOps` extraction.
  const { linkDeleteTarget, linkDeleting, askDeleteLink, cancelDeleteLink, confirmDeleteLink } = useLinkDelete({
    scheduleId, selectedLinkId, dependencies: detail.dependencies, byId, externalById, tree, run,
  })

  // Task 17: one memoised bundle for every out-of-tree consumer (the toolbar's editing group, the
  // activity panel) — built from pieces already computed above. Memoised so identity only changes
  // when one of its pieces actually does, keeping the effect below (and everything downstream of
  // `GanttOpsContext`) from re-running on every render.
  const ops = useMemo<GanttOps>(
    () => ({ rowOps, run, predecessorsByActivity, successorsByActivity, rowNumberById: tree.rowNumberById, idByRowNumber: tree.idByRowNumber, focusActivity }),
    [rowOps, run, predecessorsByActivity, successorsByActivity, tree, focusActivity],
  )

  // Report `ops` upwards (Task 16/17); `detail/index.tsx` holds it in state since `GanttToolbar`
  // and the activity panel render as siblings, not descendants, of this component.
  useEffect(() => { onOps?.(ops) }, [ops, onOps])
  useEffect(() => () => { onOps?.(null) }, [onOps])

  const onKeyDown = useGanttKeyboard({
    rows, rowIndexById, tree, focusRow, startEdit, startEditEdge, onShowShortcuts,
    onRowOp: canEdit
      ? (op: GanttRowOp) => {
        if (op === 'delete' && selectedLinkId) { askDeleteLink(); return } // a link is selected: delete it, not the row
        // Review fix (Important #4, spec §10): Insert needs `canCreate`, same as the toolbar/context-menu Add items.
        if (op === 'insert') { if (canCreate) rowOps.addActivity('task') }
        else if (op === 'delete') rowOps.remove()
        else if (op === 'indent') rowOps.indent()
        else if (op === 'outdent') rowOps.outdent()
        else if (op === 'moveUp') rowOps.moveUp()
        else if (op === 'moveDown') rowOps.moveDown()
      }
      : undefined,
  })

  const onToggle = useCallback((id: string) => api.getState().toggleCollapsed(id), [api])
  const onRowClick = useCallback((id: string, e: React.MouseEvent) => {
    api.getState().select(id, e.shiftKey ? 'range' : e.ctrlKey || e.metaKey ? 'toggle' : 'replace', visibleOrder)
  }, [api, visibleOrder])
  const onRowDoubleClick = useCallback((id: string, column: ColumnId) => startEdit(id, column), [startEdit])
  const [contextMenu, setContextMenu] = useState<RowContextMenuState | null>(null)
  const onRowContextMenu = useCallback((id: string, e: React.MouseEvent) => {
    e.preventDefault()
    const st = api.getState()
    if (!st.selectedIds.includes(id)) st.select(id, 'replace', visibleOrder)
    // already part of a multi-selection: keep it, but still point `rowOps` (keyed on `focusedId`)
    // at the row the menu is actually anchored on — every `select()` mode sets `focusedId`, but
    // skipping `select()` here to preserve the selection would otherwise leave a stale focus (e.g.
    // a shift-range's far endpoint) driving the menu's single-row Indent/Outdent/Move/Add items.
    else st.setFocused(id)
    setContextMenu({ activityId: id, x: e.clientX, y: e.clientY })
  }, [api, visibleOrder])
  // Task 2 (Freelo #32253593): the splitter no longer owns a `gridWidth` field to write — it
  // resizes the `name` column by however many px the drag moved the grid's right edge.
  const onSetGridWidth = useCallback(
    (px: number) => api.getState().setColumnWidth('name', columnWidths.name + (px - gridWidth)),
    [api, columnWidths.name, gridWidth],
  )
  const onResizeColumn = useCallback((id: ColumnId, px: number) => api.getState().setColumnWidth(id, px), [api])

  // Auto-fit (double-click a header edge): measured with a throwaway <canvas> 2D context whose font
  // mirrors the grid's own text so the measurement matches what's actually on screen; created once
  // and reused, never re-created per call.
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const measureText = useCallback((s: string): number => {
    if (!measureCanvasRef.current) measureCanvasRef.current = document.createElement('canvas')
    const ctx = measureCanvasRef.current.getContext('2d')
    if (!ctx) return 0
    ctx.font = (scrollerRef.current && getComputedStyle(scrollerRef.current).font) || '14px system-ui'
    return ctx.measureText(s).width
  }, [])

  const onAutoFitColumn = useCallback((id: ColumnId) => {
    if (id === 'health') return // icon only — nothing to measure
    const sample = rows.slice(0, 2000)

    if (id === 'name') {
      // The name column needs per-row extra width (the cell's own 8px left padding, its indent, and
      // the 20px allowance for the expand/collapse chevron and the milestone/summary icon) baked in
      // BEFORE the shared padding/clamp — `autoFitWidth` stays generic/pure (fixed signature, tested
      // on its own in `column-autofit.test.ts`), so that per-row extra is computed by hand here and
      // handed to `autoFitWidth` as a single already-padded pseudo value (a `measure` that ignores
      // its input and always returns the precomputed widest).
      const widest = sample.reduce((max, r) => Math.max(max, measureText(r.node.activity.name) + 8 + r.node.depth * INDENT_PX + 20), 0)
      api.getState().setColumnWidth('name', autoFitWidth([''], () => widest, { padding: AUTOFIT_PADDING, min: COLUMN_MIN_WIDTHS.name, max: AUTOFIT_MAX }))
      return
    }

    // Every other column: the same text the cell itself renders, one per currently visible row.
    const texts = sample.map((r) => {
      const a = r.node.activity
      switch (id) {
        case 'rowNumber': return String(r.node.rowNumber)
        case 'wbs': return a.wbsCode
        case 'owner': return a.owner ?? ''
        case 'duration': return formatDays(t, a.durationDays)
        case 'start': return formatIsoDate(t, a.start)
        case 'finish': return formatIsoDate(t, a.finish)
        case 'predecessors': { const p = predecessorsByActivity.get(a.id); return p ? formatPredecessors(p.local, tree) : '' }
        case 'percent': return String(a.isSummary ? a.rolledPercent : a.percentComplete)
        case 'float': return String(a.totalFloatDays)
        default: return ''
      }
    })
    api.getState().setColumnWidth(id, autoFitWidth(texts, measureText, { padding: AUTOFIT_PADDING, min: COLUMN_MIN_WIDTHS[id], max: AUTOFIT_MAX }))
  }, [rows, predecessorsByActivity, tree, t, measureText, api])

  const layout: GanttLayout = { rows, rowIndexById, range, pxPerDay, gridWidth, viewportWidth, viewportHeight, scrollLeft, scrollTop, today, timelineWidth, scrollerRef, tiers }
  const virtualItems = virtualizer.getVirtualItems()

  return (
    <GanttLayoutContext.Provider value={layout}>
      <GanttHoverProvider byId={byId} externalById={externalById} dependencies={detail.dependencies} calendarsById={calendarsById} defaultCalendarId={detail.schedule.defaultCalendarId}>
        <div className="flex-1 min-w-0 relative">
          <div
            ref={scrollerRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="absolute inset-0 overflow-auto outline-none focus-visible:ring-1 focus-visible:ring-primary/40 bg-background"
          >
            <div
              className="relative"
              // ui-drift: dynamic
              style={{ width: gridWidth + timelineWidth, height: contentHeight }}
            >
              <div
                // review fix (parked minor): HEADER_H is a fixed constant (48px), not a runtime
                // value — a Tailwind class replaces the inline style the `ui-drift: dynamic`
                // marker used to (incorrectly) cover.
                className="sticky top-0 z-30 flex w-full bg-background h-12"
              >
                <GanttGridHeader columns={columns} columnWidths={columnWidths} gridWidth={gridWidth} onResize={onResizeColumn} onAutoFit={onAutoFitColumn} />
                <GanttTimelineHeader />
              </div>
              <GanttGrid
                rows={rows}
                virtualItems={virtualItems}
                columns={columns}
                columnWidths={columnWidths}
                gridWidth={gridWidth}
                selectedIds={selectedIds}
                focusedId={focusedId}
                readOnly={!canEdit}
                tree={tree}
                predecessorsByActivity={predecessorsByActivity}
                editingCell={editingCell}
                pending={pending}
                onToggle={onToggle}
                onClick={onRowClick}
                onDoubleClick={onRowDoubleClick}
                onContextMenu={onRowContextMenu}
                onCommitCell={onCommitCell}
                onCancelCell={onCancelCell}
              />
              <Splitter gridWidth={gridWidth} onChange={onSetGridWidth} />
              <GanttTimeline
                virtualItems={virtualItems} detail={detail} byId={byId} externalById={externalById} canEdit={canEdit}
                tree={tree} ghostsByRow={ghostsByRow} onGhostClick={onGhostClick} barDrag={barDrag}
              />
            </div>
          </div>
        </div>
      </GanttHoverProvider>
      <RowContextMenu
        state={contextMenu}
        onClose={() => setContextMenu(null)}
        ops={rowOps}
        canEdit={canEdit}
        tree={tree}
        byId={byId}
        collapsedIds={collapsedIds}
        predecessorsByActivity={predecessorsByActivity}
        successorsByActivity={successorsByActivity}
        onSetCollapsed={(ids, collapsed) => api.getState().setCollapsed(ids, collapsed)}
        onNavigateExternal={goToExternal}
      />
      <ConfirmDialog
        open={!!deleteTargets}
        title={t('Schedule_confirm_delete_activities_title')}
        description={t('Schedule_confirm_delete_activities_body', { count: deleteCount })}
        confirmLabel={t('Schedule_op_delete')}
        variant="destructive"
        submitting={deleting}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
      {/* Task 8: built only while the dialog is open, so the hot render path stays untouched. */}
      {printSource && onPrintOpenChange && <PrintDialog open={!!printOpen} onOpenChange={onPrintOpenChange} source={printSource} />}
      <ConfirmDialog
        open={!!linkDeleteTarget}
        title={t('Schedule_link_delete_title')}
        description={linkDeleteTarget ? t('Schedule_link_delete_body', { from: linkDeleteTarget.from, to: linkDeleteTarget.to }) : undefined}
        confirmLabel={t('Common_delete')}
        variant="destructive"
        submitting={linkDeleting}
        onCancel={cancelDeleteLink}
        onConfirm={confirmDeleteLink}
      />
    </GanttLayoutContext.Provider>
  )
})
GanttView.displayName = 'GanttView'

export default GanttView
